import { useState, useRef, useCallback, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { v4 as uuid } from 'uuid';
import {
  Type, Music, Hash, AlignLeft, StickyNote, Image, Minus, Repeat,
  Save, Download, History, Heading1, Clock, Key, Check, AlertTriangle, Loader2,
  ExternalLink, PanelRightClose, PanelRightOpen, MousePointer2, Undo2, Redo2,
  AlignHorizontalJustifyStart, AlignHorizontalJustifyCenter, AlignHorizontalJustifyEnd,
  AlignVerticalJustifyStart, AlignVerticalJustifyCenter, AlignVerticalJustifyEnd,
  AlignHorizontalSpaceBetween, AlignVerticalSpaceBetween,
  Lock, LockOpen, LayoutTemplate, ListOrdered
} from 'lucide-react';
import type { ChartElement, CanvasJSON, ChartElementType, ChartMetadata, SaveStatus } from '@shared/types';
import { ElementRenderer } from './ElementRenderer';
import { InspectorPanel } from './InspectorPanel';
import { Tooltip } from './Tooltip';
import { api } from '../lib/api';
import { autoSizeElement } from '../lib/measureElement';

interface ChartEditorProps {
  chartType: 'base' | 'personal';
  chartId: number;
  songId?: number;
  initialCanvas: CanvasJSON;
  initialMetadata: ChartMetadata | Partial<ChartMetadata>;
  onSave?: (canvas: CanvasJSON, metadata: ChartMetadata | Partial<ChartMetadata>, createVersion?: boolean) => Promise<void>;
  onExportPDF?: () => void;
  onShowVersions?: () => void;
  readOnly?: boolean;
}

const ELEMENT_DEFAULT_CONTENT: Record<ChartElementType, Record<string, any>> = {
  title:      { text: 'Song Title', fontSize: 24, alignment: 'left' },
  tempo:      { bpm: 120, label: 'Tempo' },
  key:        { key: 'C', label: 'Key' },
  section:    { type: 'verse', label: 'Verse', customLabel: '', style: 'header-bar', fontSize: 14, alignment: 'left' },
  chord:      { chord: 'Am', mode: 'standard', displayStyle: 'plain', fontSize: 18 },
  lyrics:     { text: '', fontSize: 14, alignment: 'left', fontStyle: 'italic' },
  notes:      { text: '', fontSize: 14, alignment: 'left' },
  freetext:   { text: '', fontSize: 14, alignment: 'left' },
  image:      { url: '', filename: '' },
  divider:    { label: '', orientation: 'horizontal' },
  repeat:     { count: 2, label: '' },
  link:       { label: 'Reference Recording', url: '' },
  songHeader: { title: 'Song Title', artist: '', key: '', bpm: null, protected: false },
  songForm:   { mode: 'names', namesEntries: ['Intro'], abcEntries: ['A'] },
};

type ToolbarEntry = { type: ChartElementType; icon: any; label: string } | null;

const TOOLBAR_ITEMS: ToolbarEntry[] = [
  // Structure
  { type: 'songHeader', icon: LayoutTemplate, label: 'Song Header' },
  { type: 'songForm',   icon: ListOrdered,    label: 'Song Form' },
  { type: 'section',    icon: Hash,           label: 'Section' },
  null,
  // Music content
  { type: 'chord',  icon: Music,  label: 'Chord' },
  { type: 'repeat', icon: Repeat, label: 'Repeat' },
  null,
  // Text content
  { type: 'lyrics',   icon: AlignLeft,  label: 'Lyrics' },
  { type: 'freetext', icon: Type,       label: 'Text' },
  { type: 'notes',    icon: StickyNote, label: 'Notes' },
  { type: 'divider',  icon: Minus,      label: 'Divider' },
  null,
  // Metadata
  { type: 'title', icon: Heading1, label: 'Title' },
  { type: 'tempo', icon: Clock,    label: 'Tempo' },
  { type: 'key',   icon: Key,      label: 'Key' },
  null,
  // Media / external
  { type: 'image', icon: Image,       label: 'Image' },
  { type: 'link',  icon: ExternalLink, label: 'Link' },
];

const COLUMN_BUFFER = 8;

function getColumnBounds(
  elX: number,
  elY: number,
  elWidth: number,
  elHeight: number,
  elements: ChartElement[],
  canvasWidth: number,
): { left: number; right: number; maxWidth: number } | null {
  const elTop = elY;
  const elBottom = elY + elHeight;
  const canvasMargin = 36;

  const vDividers = elements.filter(e =>
    e.type === 'divider' &&
    e.content.orientation === 'vertical' &&
    e.y < elBottom &&
    e.y + e.height > elTop
  );

  if (vDividers.length === 0) return null;

  const elCenterX = elX + elWidth / 2;
  const leftDividers = vDividers.filter(d => d.x + d.width <= elCenterX);
  const rightDividers = vDividers.filter(d => d.x >= elCenterX);

  const left = leftDividers.length > 0
    ? Math.max(...leftDividers.map(d => d.x + d.width)) + COLUMN_BUFFER
    : canvasMargin;

  const right = rightDividers.length > 0
    ? Math.min(...rightDividers.map(d => d.x)) - COLUMN_BUFFER
    : canvasWidth - canvasMargin;

  return { left, right, maxWidth: Math.max(0, right - left) };
}

function computeInsertPushDown(
  elements: ChartElement[],
  newElId: string,
  insertY: number,
  pushAmount: number,
  newElX: number,
  newElRight: number,
  newElTop: number,
  newElBottom: number,
): ChartElement[] {
  // Vertical dividers only — horizontal dividers move down like any other element
  const vDividers = elements.filter(
    el => el.type === 'divider' && el.content.orientation === 'vertical'
  );

  return elements.map(el => {
    if (el.id === newElId) return el;
    if (el.y < insertY) return el;
    const separated = vDividers.some(vd => {
      const divL = vd.x, divR = vd.x + vd.width;
      const divTop = vd.y, divBottom = vd.y + vd.height;
      // Divider must vertically overlap BOTH the inserting element and the target element
      const newElOverlapsDiv = newElBottom > divTop && newElTop < divBottom;
      const targetOverlapsDiv = (el.y + el.height) > divTop && el.y < divBottom;
      if (!newElOverlapsDiv || !targetOverlapsDiv) return false;
      return (newElRight <= divL && el.x >= divR) || (newElX >= divR && el.x + el.width <= divL);
    });
    if (separated) return el;
    return { ...el, y: el.y + pushAmount };
  });
}

export function ChartEditor({ chartType, chartId, songId, initialCanvas, initialMetadata, onSave, onExportPDF, onShowVersions, readOnly = false }: ChartEditorProps) {
  const [elements, setElements] = useState<ChartElement[]>(initialCanvas?.elements || []);
  const [canvasWidth] = useState(initialCanvas?.canvasWidth || 800);
  const DEFAULT_CANVAS_HEIGHT = 1100;
  const EXPAND_THRESHOLD = 100;
  const EXPAND_INCREMENT = 300;
  const [canvasHeight, setCanvasHeight] = useState(initialCanvas?.canvasHeight || DEFAULT_CANVAS_HEIGHT);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [showToolbar, setShowToolbar] = useState(true);
  const [inspectorCollapsed, setInspectorCollapsed] = useState(false);
  const [marquee, setMarquee] = useState<{ startX: number; startY: number; currentX: number; currentY: number } | null>(null);
  const [selectMode, setSelectMode] = useState(false);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const snapToGridRef = useRef(true);
  snapToGridRef.current = snapToGrid;
  const [pendingEditId, setPendingEditId] = useState<string | null>(null);
  const [locked, setLocked] = useState(!readOnly);
  const isEditable = !readOnly && !locked;
  const ZOOM_LEVELS = [50, 75, 100, 125, 150, 200];
  const [zoom, setZoom] = useState(100);
  const zoomFactor = zoom / 100;

  const handleZoomChange = useCallback((newZoom: number) => {
    const sc = scrollContainerRef.current;
    if (sc) {
      const oldZoom = zoomRef.current;
      const newZ = newZoom / 100;
      const centerX = (sc.scrollLeft + sc.clientWidth / 2) / oldZoom;
      const centerY = (sc.scrollTop + sc.clientHeight / 2) / oldZoom;
      setZoom(newZoom);
      requestAnimationFrame(() => {
        sc.scrollLeft = centerX * newZ - sc.clientWidth / 2;
        sc.scrollTop = centerY * newZ - sc.clientHeight / 2;
      });
    } else {
      setZoom(newZoom);
    }
  }, []);
  const canvasRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const replaceImageRef = useRef<HTMLInputElement>(null);
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedIndicatorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveInFlightRef = useRef(false);
  const savePendingRef = useRef(false);
  const elementsRef = useRef(elements);
  elementsRef.current = elements;
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;
  const undoStackRef = useRef<ChartElement[][]>([]);
  const redoStackRef = useRef<ChartElement[][]>([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const MAX_UNDO = 50;
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [toolbarDrag, setToolbarDrag] = useState<{ type: ChartElementType } | null>(null);
  const toolbarDragTypeRef = useRef<ChartElementType | null>(null);
  const [dropIndicator, setDropIndicator] = useState<{
    mode: 'insert-above' | 'insert-below' | 'empty';
    targetId?: string;
    x: number; y: number; w: number; h: number;
  } | null>(null);

  const pushUndo = useCallback((snapshot: ChartElement[]) => {
    undoStackRef.current = [...undoStackRef.current.slice(-(MAX_UNDO - 1)), snapshot.map(el => ({ ...el, content: { ...el.content } }))];
    redoStackRef.current = [];
    setCanUndo(true);
    setCanRedo(false);
  }, []);

  const undo = useCallback(() => {
    if (undoStackRef.current.length === 0) return;
    const prev = undoStackRef.current[undoStackRef.current.length - 1];
    undoStackRef.current = undoStackRef.current.slice(0, -1);
    redoStackRef.current = [...redoStackRef.current, elementsRef.current.map(el => ({ ...el, content: { ...el.content } }))];
    setElements(prev);
    elementsRef.current = prev;
    clearSelection();
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(true);
    markDirty();
  }, []);

  const redo = useCallback(() => {
    if (redoStackRef.current.length === 0) return;
    const next = redoStackRef.current[redoStackRef.current.length - 1];
    redoStackRef.current = redoStackRef.current.slice(0, -1);
    undoStackRef.current = [...undoStackRef.current, elementsRef.current.map(el => ({ ...el, content: { ...el.content } }))];
    setElements(next);
    elementsRef.current = next;
    clearSelection();
    setCanUndo(true);
    setCanRedo(redoStackRef.current.length > 0);
    markDirty();
  }, []);

  const interactionRef = useRef<{
    mode: 'none' | 'drag' | 'resize' | 'marquee';
    handle: string;
    startX: number;
    startY: number;
    startElX: number;
    startElY: number;
    startElW: number;
    startElH: number;
    elementId: string | null;
    groupOffsets: { id: string; dx: number; dy: number }[];
  }>({
    mode: 'none', handle: '', startX: 0, startY: 0,
    startElX: 0, startElY: 0, startElW: 0, startElH: 0, elementId: null,
    groupOffsets: [],
  });

  const selectSingle = useCallback((id: string) => {
    setSelectedIds(new Set([id]));
  }, []);

  const toggleSelection = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const deleteSelected = useCallback(() => {
    pushUndo(elementsRef.current);
    setElements(prev => prev.filter(el => !selectedIdsRef.current.has(el.id)));
    clearSelection();
    markDirty();
  }, []);

  const isEditableRef = useRef(isEditable);
  isEditableRef.current = isEditable;

  useEffect(() => {
    if (readOnly) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
      if (!isEditableRef.current) return;
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
        return;
      }
      if (e.key === 'Delete' && selectedIds.size > 0 && !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement)) {
        if (selectedIds.size === 1) {
          deleteElement([...selectedIds][0]);
        } else {
          deleteSelected();
        }
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'd' && selectedIds.size === 1) {
        e.preventDefault();
        duplicateElement([...selectedIds][0]);
      }
      if (e.key === 'Escape') {
        clearSelection();
        if (selectMode) setSelectMode(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIds, elements, readOnly, selectMode]);

  useEffect(() => {
    const handleGlobalMove = (e: MouseEvent | TouchEvent) => {
      const ir = interactionRef.current;

      if (ir.mode === 'marquee') {
        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const z = zoomRef.current;
        setMarquee(prev => prev ? {
          ...prev,
          currentX: (clientX - rect.left) / z,
          currentY: (clientY - rect.top) / z,
        } : null);
        return;
      }

      if (ir.mode === 'none' || !ir.elementId) return;

      if ('touches' in e) e.preventDefault();

      const rawClientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const rawClientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const z = zoomRef.current;
      const canvasX = (rawClientX - rect.left) / z;
      const canvasY = (rawClientY - rect.top) / z;
      const dx = canvasX - ir.startX;
      const dy = canvasY - ir.startY;

      const SNAP_GRID = 8;
      const SNAP_MARGIN = 36;
      const SNAP_THRESHOLD = 8;

      const snapPos = (v: number): number => {
        if (!snapToGridRef.current) return v;
        const snapped = Math.max(SNAP_MARGIN, SNAP_MARGIN + Math.round((v - SNAP_MARGIN) / SNAP_GRID) * SNAP_GRID);
        return Math.abs(v - snapped) <= SNAP_THRESHOLD ? snapped : v;
      };

      const snapDim = (v: number, min: number): number => {
        if (!snapToGridRef.current) return Math.max(min, v);
        return Math.max(min, Math.round(v / SNAP_GRID) * SNAP_GRID);
      };

      if (ir.mode === 'drag') {
        if (ir.groupOffsets.length > 0) {
          setElements(prev => {
            const next = prev.map(el => {
              const offset = ir.groupOffsets.find(o => o.id === el.id);
              if (!offset) return el;
              const rawX = Math.max(0, offset.dx + dx);
              const rawY = Math.max(0, offset.dy + dy);
              return { ...el, x: snapPos(rawX), y: snapPos(rawY) };
            });
            maybeExpandCanvas(next);
            return next;
          });
          markDirty();
        } else {
          const rawX = Math.max(0, ir.startElX + dx);
          const rawY = Math.max(0, ir.startElY + dy);
          let newX = snapPos(rawX);
          const newY = snapPos(rawY);
          // Column constraint: clamp x within column bounds; auto-snaps into adjacent column when crossing a divider
          const draggedEl = elementsRef.current.find(e => e.id === ir.elementId);
          if (draggedEl) {
            const others = elementsRef.current.filter(e => e.id !== ir.elementId);
            const colBounds = getColumnBounds(newX, newY, draggedEl.width, draggedEl.height, others, canvasWidth);
            if (colBounds) newX = Math.max(colBounds.left, Math.min(colBounds.right - draggedEl.width, newX));
          }
          updateElement(ir.elementId, { x: newX, y: newY });
          maybeExpandCanvas(elementsRef.current.map(el => el.id === ir.elementId ? { ...el, x: newX, y: newY } : el));
        }
      } else if (ir.mode === 'resize') {
        let newX = ir.startElX;
        let newY = ir.startElY;
        let newW = ir.startElW;
        let newH = ir.startElH;
        const h = ir.handle;

        if (h.includes('e')) newW = snapDim(ir.startElW + dx, 30);
        if (h.includes('w')) { newW = snapDim(ir.startElW - dx, 30); newX = ir.startElX + ir.startElW - newW; }
        if (h.includes('s')) newH = snapDim(ir.startElH + dy, 20);
        if (h.includes('n')) { newH = snapDim(ir.startElH - dy, 20); newY = ir.startElY + ir.startElH - newH; }

        updateElement(ir.elementId, { x: newX, y: newY, width: newW, height: newH });
        maybeExpandCanvas(elementsRef.current.map(el => el.id === ir.elementId ? { ...el, x: newX, y: newY, width: newW, height: newH } : el));
      }
    };

    const handleGlobalUp = () => {
      const ir = interactionRef.current;

      if (ir.mode === 'resize' && ir.elementId) {
        const resizedId = ir.elementId;
        setElements(prev => prev.map(e => e.id === resizedId ? { ...e, manualSize: true } : e));
        markDirty();
      }

      if (ir.mode === 'marquee') {
        setMarquee(prev => {
          if (!prev) return null;
          const x1 = Math.min(prev.startX, prev.currentX);
          const y1 = Math.min(prev.startY, prev.currentY);
          const x2 = Math.max(prev.startX, prev.currentX);
          const y2 = Math.max(prev.startY, prev.currentY);

          const hits = elementsRef.current.filter(el => {
            const elRight = el.x + el.width;
            const elBottom = el.y + el.height;
            return el.x < x2 && elRight > x1 && el.y < y2 && elBottom > y1;
          });

          const hasArea = Math.max(x2 - x1, y2 - y1) > 4;
          if (hasArea) {
            setSelectedIds(new Set(hits.map(h => h.id)));
          } else {
            setSelectedIds(new Set());
          }
          return null;
        });
        setMarquee(null);
      }

      interactionRef.current = {
        ...interactionRef.current,
        mode: 'none',
        elementId: null,
        groupOffsets: [],
      };
    };

    const preventSelectStart = (e: Event) => {
      if (interactionRef.current.mode !== 'none') e.preventDefault();
    };

    window.addEventListener('mousemove', handleGlobalMove);
    window.addEventListener('mouseup', handleGlobalUp);
    window.addEventListener('touchmove', handleGlobalMove, { passive: false });
    window.addEventListener('touchend', handleGlobalUp);

    const sc = scrollContainerRef.current;
    if (sc) sc.addEventListener('selectstart', preventSelectStart);

    return () => {
      window.removeEventListener('mousemove', handleGlobalMove);
      window.removeEventListener('mouseup', handleGlobalUp);
      window.removeEventListener('touchmove', handleGlobalMove);
      window.removeEventListener('touchend', handleGlobalUp);
      if (sc) sc.removeEventListener('selectstart', preventSelectStart);
    };
  }, []);

  const CANVAS_MARGIN = 36;
  const HEADER_MAX_WIDTH = canvasWidth - CANVAS_MARGIN * 2;
  const ALIGN_TOLERANCE = 8;
  const STACK_GAP = 8;

  const addElement = useCallback((type: ChartElementType) => {
    const defaultContent = { ...ELEMENT_DEFAULT_CONTENT[type] };

    // Song Header: first one becomes the linked/protected header auto-populated
    // from song metadata; subsequent ones are plain unlinked headers.
    let forceTopPosition = false;
    if (type === 'songHeader') {
      const hasLinkedHeader = elementsRef.current.some(
        el => el.type === 'songHeader' && el.content.protected === true
      );
      if (!hasLinkedHeader) {
        const meta = initialMetadata as any;
        defaultContent.title = meta.songTitle || '';
        defaultContent.artist = meta.artist || '';
        defaultContent.key = meta.musicalKey || '';
        defaultContent.bpm = meta.tempo ?? null;
        defaultContent.protected = true;
        forceTopPosition = true;
      }
      // else: leave defaultContent as-is (protected: false) — unlinked header
    }

    const isHeaderType = type === 'songHeader' || type === 'songForm';
    let { width: initW, height: initH } = type === 'image'
      ? { width: 300, height: 200 }
      : type === 'divider'
        ? { width: 300, height: 12 }
        : autoSizeElement(type, defaultContent, isHeaderType ? HEADER_MAX_WIDTH : canvasWidth * 0.5);

    // Song Form: compact 150px editing width; display auto-sizes on deselect.
    if (type === 'songForm') {
      initW = 150;
      initH = Math.max(initH, 90);
    }

    let placeX: number;
    let placeY: number;

    if (forceTopPosition) {
      placeX = CANVAS_MARGIN;
      placeY = CANVAS_MARGIN;
    } else if (type === 'songForm') {
      const existingHeader = elementsRef.current.find(el => el.type === 'songHeader');
      if (existingHeader) {
        placeX = existingHeader.x + existingHeader.width + STACK_GAP;
        placeY = existingHeader.y;
      } else {
        placeX = CANVAS_MARGIN;
        placeY = CANVAS_MARGIN;
      }
    } else if (type === 'lyrics') {
      const usableWidth = canvasWidth - CANVAS_MARGIN * 2;
      placeX = Math.round(CANVAS_MARGIN + usableWidth / 2);
      placeY = CANVAS_MARGIN;
    } else {
      // Header area = max bottom edge of songHeader / songForm elements
      const headerBottom = elementsRef.current
        .filter(el => el.type === 'songHeader' || el.type === 'songForm')
        .reduce((max, el) => Math.max(max, el.y + el.height), 0);
      const leftAligned = elementsRef.current.filter(
        el => Math.abs(el.x - CANVAS_MARGIN) <= ALIGN_TOLERANCE
      );
      if (leftAligned.length > 0) {
        const lowest = leftAligned.reduce((best, el) =>
          el.y + el.height > best.y + best.height ? el : best
        );
        placeX = CANVAS_MARGIN;
        placeY = Math.max(
          lowest.y + lowest.height + STACK_GAP,
          headerBottom > 0 ? headerBottom + STACK_GAP : 0
        );
      } else {
        placeX = CANVAS_MARGIN;
        placeY = headerBottom > 0 ? headerBottom + STACK_GAP : CANVAS_MARGIN;
      }
    }

    // Nudge placement to avoid overlapping existing dividers
    for (const divider of elementsRef.current.filter(el => el.type === 'divider')) {
      const dOrientation = divider.content.orientation || 'horizontal';
      if (dOrientation === 'horizontal') {
        const divBottom = divider.y + divider.height;
        if (placeX < divider.x + divider.width && placeX + initW > divider.x) {
          if (placeY < divBottom && placeY + initH > divider.y) {
            placeY = divBottom + STACK_GAP;
          }
        }
      } else {
        const divRight = divider.x + divider.width;
        if (placeY < divider.y + divider.height && placeY + initH > divider.y) {
          if (placeX < divRight && placeX + initW > divider.x) {
            placeX = divRight + STACK_GAP;
          }
        }
      }
    }

    // Re-measure with column-aware maxWidth once the placement position is known
    if (type !== 'image' && type !== 'divider') {
      const colBounds = getColumnBounds(placeX, placeY, initW, initH, elementsRef.current, canvasWidth);
      if (colBounds) ({ width: initW, height: initH } = autoSizeElement(type, defaultContent, colBounds.maxWidth));
    }

    const newElement: ChartElement = {
      id: uuid(),
      type,
      x: placeX,
      y: placeY,
      width: initW,
      height: initH,
      content: defaultContent,
      zIndex: elements.length,
      manualSize: false,
    };

    if (type === 'image') {
      fileInputRef.current?.click();
      const handler = async (e: Event) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) return;
        try {
          const result = await api.images.upload(file, chartType, chartId);
          newElement.content = { url: result.url, filename: result.filename };
          pushUndo(elementsRef.current);
          setElements(prev => {
            const next = [...prev, newElement];
            maybeExpandCanvas(next);
            return next;
          });
          selectSingle(newElement.id);
          markDirty();
        } catch (err) {
          console.error('Upload failed:', err);
        }
        fileInputRef.current!.removeEventListener('change', handler);
        fileInputRef.current!.value = '';
      };
      fileInputRef.current?.addEventListener('change', handler);
      return;
    }

    pushUndo(elementsRef.current);
    const NON_EDITABLE_TYPES = new Set(['image', 'divider', 'songHeader']);
    const willAutoEdit = !NON_EDITABLE_TYPES.has(type) && isEditableRef.current;
    // flushSync forces a synchronous DOM update within the click-handler gesture so
    // the editing input mounts while the browser still considers the touch/click active.
    // This is what allows the mobile virtual keyboard to open automatically.
    flushSync(() => {
      setElements(prev => {
        const next = [...prev, newElement];
        maybeExpandCanvas(next);
        return next;
      });
      selectSingle(newElement.id);
      setSelectMode(false);
      if (willAutoEdit) {
        setPendingEditId(newElement.id);
      }
    });
    markDirty();
  }, [elements.length, chartType, chartId]);

  const deleteElement = useCallback((id: string) => {
    const el = elementsRef.current.find(e => e.id === id);
    if (el?.type === 'songHeader' && el.content.protected) {
      setPendingDeleteId(id);
      return;
    }
    pushUndo(elementsRef.current);
    setElements(prev => prev.filter(e => e.id !== id));
    clearSelection();
    markDirty();
  }, []);

  const duplicateElement = useCallback((id: string) => {
    pushUndo(elementsRef.current);
    setElements(prev => {
      const source = prev.find(el => el.id === id);
      if (!source) return prev;
      const dup: ChartElement = {
        ...source,
        id: uuid(),
        x: source.x + 20,
        y: source.y + 20,
        content: { ...source.content },
        zIndex: prev.length,
      };
      selectSingle(dup.id);
      markDirty();
      const next = [...prev, dup];
      maybeExpandCanvas(next);
      return next;
    });
  }, []);

  const updateElement = useCallback((id: string, updates: Partial<ChartElement>) => {
    setElements(prev => prev.map(el => el.id === id ? { ...el, ...updates } : el));
    markDirty();
  }, []);

  const canvasHeightRef = useRef(canvasHeight);
  canvasHeightRef.current = canvasHeight;

  const maybeExpandCanvas = useCallback((elems: ChartElement[]) => {
    const maxBottom = elems.reduce((max, el) => Math.max(max, el.y + el.height), 0);
    const currentHeight = canvasHeightRef.current;
    if (maxBottom > currentHeight - EXPAND_THRESHOLD) {
      const newHeight = maxBottom + EXPAND_INCREMENT;
      setCanvasHeight(newHeight);
    }
  }, []);

  const alignElements = useCallback((action: string) => {
    const ids = selectedIdsRef.current;
    if (ids.size < 2) return;
    const selected = elementsRef.current.filter(el => ids.has(el.id));
    if (selected.length < 2) return;

    pushUndo(elementsRef.current);

    const minX = Math.min(...selected.map(el => el.x));
    const maxX = Math.max(...selected.map(el => el.x + el.width));
    const minY = Math.min(...selected.map(el => el.y));
    const maxY = Math.max(...selected.map(el => el.y + el.height));
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    setElements(prev => prev.map(el => {
      if (!ids.has(el.id)) return el;
      switch (action) {
        case 'left': return { ...el, x: minX };
        case 'center': return { ...el, x: centerX - el.width / 2 };
        case 'right': return { ...el, x: maxX - el.width };
        case 'top': return { ...el, y: minY };
        case 'middle': return { ...el, y: centerY - el.height / 2 };
        case 'bottom': return { ...el, y: maxY - el.height };
        default: return el;
      }
    }));
    markDirty();
  }, []);

  const distributeElements = useCallback((axis: 'horizontal' | 'vertical') => {
    const ids = selectedIdsRef.current;
    if (ids.size < 3) return;
    const selected = elementsRef.current.filter(el => ids.has(el.id));
    if (selected.length < 3) return;

    pushUndo(elementsRef.current);

    if (axis === 'horizontal') {
      const sorted = [...selected].sort((a, b) => a.x - b.x);
      const totalWidth = sorted.reduce((sum, el) => sum + el.width, 0);
      const spanStart = sorted[0].x;
      const spanEnd = sorted[sorted.length - 1].x + sorted[sorted.length - 1].width;
      const gap = (spanEnd - spanStart - totalWidth) / (sorted.length - 1);
      let currentX = spanStart;
      const positions = new Map<string, number>();
      sorted.forEach(el => {
        positions.set(el.id, currentX);
        currentX += el.width + gap;
      });
      setElements(prev => prev.map(el => {
        const newX = positions.get(el.id);
        return newX !== undefined ? { ...el, x: newX } : el;
      }));
    } else {
      const sorted = [...selected].sort((a, b) => a.y - b.y);
      const totalHeight = sorted.reduce((sum, el) => sum + el.height, 0);
      const spanStart = sorted[0].y;
      const spanEnd = sorted[sorted.length - 1].y + sorted[sorted.length - 1].height;
      const gap = (spanEnd - spanStart - totalHeight) / (sorted.length - 1);
      let currentY = spanStart;
      const positions = new Map<string, number>();
      sorted.forEach(el => {
        positions.set(el.id, currentY);
        currentY += el.height + gap;
      });
      setElements(prev => prev.map(el => {
        const newY = positions.get(el.id);
        return newY !== undefined ? { ...el, y: newY } : el;
      }));
    }
    markDirty();
  }, []);

  const selectModeRef = useRef(selectMode);
  selectModeRef.current = selectMode;

  const zoomRef = useRef(zoomFactor);
  zoomRef.current = zoomFactor;

  const AUTO_RESIZE_MAX_WIDTH = Math.round(canvasWidth * 0.5);

  const getCanvasCoords = useCallback((clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: clientX, y: clientY };
    const rect = canvas.getBoundingClientRect();
    const z = zoomRef.current;
    return { x: (clientX - rect.left) / z, y: (clientY - rect.top) / z };
  }, []);

  const handleElementMouseDown = useCallback((e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    const el = elements.find(el => el.id === id);
    if (!el) return;

    const isAlreadySelected = selectedIdsRef.current.has(id);

    if (selectModeRef.current) {
      if (!isAlreadySelected) {
        // In Select mode, clicking unselected element toggles it — no drag
        toggleSelection(id);
        return;
      }
      // In Select mode, clicking an already-selected element arms drag for the whole group
    } else if (e.shiftKey) {
      toggleSelection(id);
      return;
    } else if (!isAlreadySelected) {
      // Normal mode first click: select only, no drag
      selectSingle(id);
      return;
    }

    // Arm drag (element is selected)
    pushUndo(elementsRef.current);

    const groupOffsets = selectedIdsRef.current.size > 1
      ? elements.filter(el => selectedIdsRef.current.has(el.id)).map(el => ({ id: el.id, dx: el.x, dy: el.y }))
      : [];

    const coords = getCanvasCoords(e.clientX, e.clientY);
    interactionRef.current = {
      mode: 'drag',
      handle: '',
      startX: coords.x,
      startY: coords.y,
      startElX: el.x,
      startElY: el.y,
      startElW: el.width,
      startElH: el.height,
      elementId: id,
      groupOffsets,
    };
  }, [elements]);

  const handleElementTouchStart = useCallback((e: React.TouchEvent, id: string) => {
    e.stopPropagation();
    const el = elements.find(el => el.id === id);
    if (!el) return;
    const touch = e.touches[0];

    const isAlreadySelected = selectedIdsRef.current.has(id);

    if (selectModeRef.current) {
      if (!isAlreadySelected) {
        // In Select mode, tapping unselected element toggles it — no drag
        toggleSelection(id);
        return;
      }
      // In Select mode, tapping already-selected element arms drag for the whole group
    } else if (!isAlreadySelected) {
      // Normal mode first tap: select only, no drag
      selectSingle(id);
      return;
    }

    // Arm drag (element is selected)
    pushUndo(elementsRef.current);

    const groupOffsets = selectedIdsRef.current.size > 1
      ? elements.filter(el => selectedIdsRef.current.has(el.id)).map(el => ({ id: el.id, dx: el.x, dy: el.y }))
      : [];

    const coords = getCanvasCoords(touch.clientX, touch.clientY);
    interactionRef.current = {
      mode: 'drag',
      handle: '',
      startX: coords.x,
      startY: coords.y,
      startElX: el.x,
      startElY: el.y,
      startElW: el.width,
      startElH: el.height,
      elementId: id,
      groupOffsets,
    };
  }, [elements]);

  const handleResizeStart = useCallback((id: string, handle: string, e: React.MouseEvent | React.TouchEvent) => {
    const el = elements.find(el => el.id === id);
    if (!el) return;
    selectSingle(id);
    pushUndo(elementsRef.current);

    const clientX = 'touches' in e ? (e as React.TouchEvent).touches[0].clientX : (e as React.MouseEvent).clientX;
    const clientY = 'touches' in e ? (e as React.TouchEvent).touches[0].clientY : (e as React.MouseEvent).clientY;
    const coords = getCanvasCoords(clientX, clientY);

    interactionRef.current = {
      mode: 'resize',
      handle,
      startX: coords.x,
      startY: coords.y,
      startElX: el.x,
      startElY: el.y,
      startElW: el.width,
      startElH: el.height,
      elementId: id,
      groupOffsets: [],
    };
  }, [elements]);

  const handleCanvasMouseDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-chart-element]')) return;
    if (!isEditableRef.current) return;

    if (!selectModeRef.current) {
      clearSelection();
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const z = zoomRef.current;
    const x = (e.clientX - rect.left) / z;
    const y = (e.clientY - rect.top) / z;

    setMarquee({ startX: x, startY: y, currentX: x, currentY: y });
    interactionRef.current = {
      mode: 'marquee',
      handle: '',
      startX: e.clientX,
      startY: e.clientY,
      startElX: x,
      startElY: y,
      startElW: 0,
      startElH: 0,
      elementId: null,
      groupOffsets: [],
    };
  }, [readOnly]);

  const handleCanvasTouchStart = useCallback((e: React.TouchEvent) => {
    if ((e.target as HTMLElement).closest('[data-chart-element]')) return;
    if (!isEditableRef.current) return;

    if (!selectModeRef.current) {
      clearSelection();
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const touch = e.touches[0];
    const rect = canvas.getBoundingClientRect();
    const z = zoomRef.current;
    const x = (touch.clientX - rect.left) / z;
    const y = (touch.clientY - rect.top) / z;

    setMarquee({ startX: x, startY: y, currentX: x, currentY: y });
    interactionRef.current = {
      mode: 'marquee',
      handle: '',
      startX: touch.clientX,
      startY: touch.clientY,
      startElX: x,
      startElY: y,
      startElW: 0,
      startElH: 0,
      elementId: null,
      groupOffsets: [],
    };
  }, [readOnly]);

  const handleCanvasDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    const dragType = toolbarDragTypeRef.current;
    if (!dragType) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const z = zoomRef.current;
    const cx = (e.clientX - rect.left) / z;
    const cy = (e.clientY - rect.top) / z;

    // Compute default element size for the empty-drop ghost
    const { width: defW, height: defH } = dragType === 'image'
      ? { width: 300, height: 200 }
      : dragType === 'divider'
        ? { width: 300, height: 12 }
        : autoSizeElement(dragType, ELEMENT_DEFAULT_CONTENT[dragType], 800 * 0.5);

    // Hit-test: find topmost element under cursor
    const hit = [...elementsRef.current]
      .sort((a, b) => b.zIndex - a.zIndex)
      .find(el => cx >= el.x && cx <= el.x + el.width && cy >= el.y && cy <= el.y + el.height);

    if (hit) {
      const isTop = cy < hit.y + hit.height / 2;
      setDropIndicator({
        mode: isTop ? 'insert-above' : 'insert-below',
        targetId: hit.id,
        x: hit.x,
        y: isTop ? hit.y : hit.y + hit.height,
        w: hit.width,
        h: 3,
      });
    } else {
      setDropIndicator({
        mode: 'empty',
        x: cx,
        y: cy,
        w: defW,
        h: defH,
      });
    }
  }, []);

  const handleCanvasDragLeave = useCallback((e: React.DragEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!canvas.contains(e.relatedTarget as Node)) {
      setDropIndicator(null);
    }
  }, []);

  const handleCanvasDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const dragType = toolbarDragTypeRef.current;
    toolbarDragTypeRef.current = null;
    setToolbarDrag(null);
    setDropIndicator(null);
    if (!dragType || !isEditableRef.current) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const z = zoomRef.current;
    const cx = (e.clientX - rect.left) / z;
    const cy = (e.clientY - rect.top) / z;

    const defaultContent = { ...ELEMENT_DEFAULT_CONTENT[dragType] };
    if (dragType === 'songHeader') {
      const hasLinked = elementsRef.current.some(el => el.type === 'songHeader' && el.content.protected === true);
      if (!hasLinked) {
        const meta = initialMetadata as any;
        defaultContent.title = meta.songTitle || '';
        defaultContent.artist = meta.artist || '';
        defaultContent.key = meta.musicalKey || '';
        defaultContent.bpm = meta.tempo ?? null;
        defaultContent.protected = true;
      }
    }

    const isDragHeaderType = dragType === 'songHeader' || dragType === 'songForm';
    let { width: initW, height: initH } = dragType === 'image'
      ? { width: 300, height: 200 }
      : dragType === 'divider'
        ? { width: 300, height: 12 }
        : autoSizeElement(dragType, defaultContent, isDragHeaderType ? HEADER_MAX_WIDTH : canvasWidth * 0.5);

    // Snap the drop position
    const SNAP_GRID = 8;
    const snapVal = (v: number, base: number) =>
      snapToGridRef.current
        ? Math.max(base, base + Math.round((v - base) / SNAP_GRID) * SNAP_GRID)
        : Math.max(base, v);

    // Hit-test to find overlapping element
    const hit = [...elementsRef.current]
      .sort((a, b) => b.zIndex - a.zIndex)
      .find(el => cx >= el.x && cx <= el.x + el.width && cy >= el.y && cy <= el.y + el.height);

    let placeX: number;
    let placeY: number;
    let doInsert = false;
    let insertY = 0;

    if (hit) {
      const isTop = cy < hit.y + hit.height / 2;
      placeX = hit.x;
      placeY = isTop ? hit.y : hit.y + hit.height + STACK_GAP;
      insertY = placeY;
      doInsert = true;
    } else {
      placeX = snapVal(cx, CANVAS_MARGIN);
      placeY = snapVal(cy, CANVAS_MARGIN);
      insertY = placeY;
    }

    // Re-measure with column-aware maxWidth at the resolved drop position
    if (dragType !== 'image' && dragType !== 'divider') {
      const colBounds = getColumnBounds(placeX, placeY, initW, initH, elementsRef.current, canvasWidth);
      if (colBounds) ({ width: initW, height: initH } = autoSizeElement(dragType, defaultContent, colBounds.maxWidth));
    }

    const newEl: ChartElement = {
      id: uuid(),
      type: dragType,
      x: placeX,
      y: placeY,
      width: initW,
      height: initH,
      content: defaultContent,
      zIndex: elementsRef.current.length,
      manualSize: false,
    };

    if (dragType === 'image') {
      fileInputRef.current?.click();
      const handler = async (ev: Event) => {
        const file = (ev.target as HTMLInputElement).files?.[0];
        if (!file) return;
        try {
          const result = await api.images.upload(file, chartType, chartId);
          newEl.content = { url: result.url, filename: result.filename };
          pushUndo(elementsRef.current);
          setElements(prev => { const next = [...prev, newEl]; maybeExpandCanvas(next); return next; });
          selectSingle(newEl.id);
          markDirty();
        } catch (err) { console.error('Upload failed:', err); }
        fileInputRef.current!.removeEventListener('change', handler);
        fileInputRef.current!.value = '';
      };
      fileInputRef.current?.addEventListener('change', handler);
      return;
    }

    pushUndo(elementsRef.current);

    let nextElements: ChartElement[];
    if (doInsert) {
      const pushed = computeInsertPushDown(
        elementsRef.current,
        newEl.id,
        insertY,
        initH + STACK_GAP,
        placeX,
        placeX + initW,
        placeY,
        placeY + initH,
      );
      nextElements = [...pushed, newEl];
    } else {
      nextElements = [...elementsRef.current, newEl];
    }

    flushSync(() => {
      setElements(nextElements);
      selectSingle(newEl.id);
      setSelectMode(false);
      const NON_EDITABLE_TYPES = new Set(['image', 'divider', 'songHeader']);
      if (!NON_EDITABLE_TYPES.has(dragType)) {
        setPendingEditId(newEl.id);
      }
    });
    maybeExpandCanvas(nextElements);
    markDirty();
  }, [canvasWidth, chartType, chartId, initialMetadata]);

  const moveLayer = (id: string, direction: 'up' | 'down') => {
    setElements(prev => {
      const sorted = [...prev].sort((a, b) => a.zIndex - b.zIndex);
      const idx = sorted.findIndex(el => el.id === id);
      if (direction === 'up' && idx < sorted.length - 1) {
        const tmp = sorted[idx].zIndex;
        sorted[idx] = { ...sorted[idx], zIndex: sorted[idx + 1].zIndex };
        sorted[idx + 1] = { ...sorted[idx + 1], zIndex: tmp };
      } else if (direction === 'down' && idx > 0) {
        const tmp = sorted[idx].zIndex;
        sorted[idx] = { ...sorted[idx], zIndex: sorted[idx - 1].zIndex };
        sorted[idx - 1] = { ...sorted[idx - 1], zIndex: tmp };
      }
      return sorted;
    });
    markDirty();
  };

  const markDirty = useCallback(() => {
    if (readOnly) return;
    setSaveStatus('unsaved');
    if (savedIndicatorTimerRef.current) {
      clearTimeout(savedIndicatorTimerRef.current);
      savedIndicatorTimerRef.current = null;
    }
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }
    autosaveTimerRef.current = setTimeout(() => {
      triggerAutosave();
    }, 2000);
  }, [readOnly]);

  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
      if (savedIndicatorTimerRef.current) clearTimeout(savedIndicatorTimerRef.current);
    };
  }, []);

  const performSave = async (currentElements: ChartElement[], createVersion = false): Promise<boolean> => {
    const maxY = currentElements.reduce((max, el) => Math.max(max, el.y + el.height), 0);
    const adjustedHeight = Math.max(canvasHeight, maxY + 60);
    if (adjustedHeight !== canvasHeight) setCanvasHeight(adjustedHeight);

    const canvas: CanvasJSON = { elements: currentElements, canvasWidth, canvasHeight: adjustedHeight, zoom: 1 };
    const meta = { ...(initialMetadata as any) };
    for (const el of currentElements) {
      if (el.type === 'title') meta.songTitle = el.content.text || meta.songTitle;
      if (el.type === 'tempo') meta.tempo = el.content.bpm || meta.tempo;
      if (el.type === 'key') meta.musicalKey = el.content.key || meta.musicalKey;
      if (el.type === 'lyrics') meta.hasLyrics = true;
      if (el.type === 'notes') meta.hasNotes = true;
      if (el.type === 'songHeader' && el.content.protected) {
        if (el.content.title) meta.songTitle = el.content.title;
        if (el.content.key) meta.musicalKey = el.content.key;
        if (el.content.bpm != null) meta.tempo = el.content.bpm;
        if (el.content.artist !== undefined) meta.artist = el.content.artist;
      }
    }
    const sections = currentElements.filter(el => el.type === 'section').map(el => ({
      type: (el.content.type || 'custom') as any,
      label: el.content.label || '',
      repeatCount: undefined,
    }));
    if (sections.length > 0) meta.sections = sections;

    if (onSave) await onSave(canvas, meta, createVersion);
    return true;
  };

  const pendingCreateVersionRef = useRef(false);

  const executeSave = useCallback(async (currentElements: ChartElement[], createVersion = false) => {
    if (saveInFlightRef.current) {
      savePendingRef.current = true;
      if (createVersion) pendingCreateVersionRef.current = true;
      return;
    }
    saveInFlightRef.current = true;
    setSaving(true);
    setSaveStatus('saving');
    try {
      await performSave(currentElements, createVersion);
      setSaveStatus(createVersion ? 'saved' : 'autosaved');
      savedIndicatorTimerRef.current = setTimeout(() => {
        setSaveStatus(prev => (prev === 'saved' || prev === 'autosaved') ? 'idle' : prev);
      }, 3000);
    } catch (err) {
      console.error('Save failed:', err);
      setSaveStatus('failed');
    } finally {
      setSaving(false);
      saveInFlightRef.current = false;
      if (savePendingRef.current) {
        const pendingVersion = pendingCreateVersionRef.current;
        savePendingRef.current = false;
        pendingCreateVersionRef.current = false;
        executeSave(elementsRef.current, pendingVersion);
      }
    }
  }, [canvasWidth, canvasHeight, initialMetadata, onSave]);

  const triggerAutosave = useCallback(async () => {
    executeSave(elementsRef.current, false);
  }, [executeSave]);

  const handleSave = async () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    executeSave(elements, true);
  };

  const handleReplaceImage = () => {
    if (selectedIds.size !== 1) return;
    const targetId = [...selectedIds][0];
    const input = replaceImageRef.current;
    if (!input) return;
    input.value = '';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const result = await api.images.upload(file, chartType, chartId);
        setElements(prev => prev.map(el =>
          el.id === targetId ? { ...el, content: { ...el.content, url: result.url, filename: result.filename } } : el
        ));
        markDirty();
      } catch (err) {
        console.error('Upload failed:', err);
      }
      input.value = '';
      input.onchange = null;
    };
    input.click();
  };

  const selectedElements = elements.filter(el => selectedIds.has(el.id));
  const singleSelected = selectedElements.length === 1 ? selectedElements[0] : null;
  const hasSelection = selectedElements.length > 0;

  const marqueeStyle = marquee ? (() => {
    const x = Math.min(marquee.startX, marquee.currentX);
    const y = Math.min(marquee.startY, marquee.currentY);
    const w = Math.abs(marquee.currentX - marquee.startX);
    const h = Math.abs(marquee.currentY - marquee.startY);
    return { left: x, top: y, width: w, height: h } as React.CSSProperties;
  })() : null;

  return (
    <div className="h-full flex flex-col bg-surface-950">
      <div className="bg-surface-900 border-b border-surface-700 flex items-center shrink-0">
        {/* Left: scrollable tool items */}
        <div className="flex-1 min-w-0 flex items-center gap-2 px-3 py-2 overflow-x-auto">
          {!readOnly && (
            <Tooltip label={locked ? 'Unlock chart for editing' : 'Lock chart to prevent edits'}>
              <button
                onClick={() => { setLocked(!locked); if (!locked) { clearSelection(); setSelectMode(false); } }}
                className={`flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded transition-colors shrink-0 ${locked ? 'bg-surface-700 text-gray-300' : 'bg-amber-600 text-white'}`}
              >
                {locked ? <Lock className="w-3.5 h-3.5" /> : <LockOpen className="w-3.5 h-3.5" />}
                <span className="hidden sm:inline">{locked ? 'Locked' : 'Editing'}</span>
              </button>
            </Tooltip>
          )}
          {isEditable && (
            <>
              <div className="w-px h-5 bg-surface-700 shrink-0" />
              <Tooltip label={selectMode ? 'Exit select mode (Esc)' : 'Select multiple elements'}>
                <button
                  onClick={() => { setSelectMode(!selectMode); if (!selectMode) clearSelection(); }}
                  className={`flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded transition-colors shrink-0 ${selectMode ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-surface-800'}`}
                >
                  <MousePointer2 className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Select</span>
                </button>
              </Tooltip>
              <Tooltip label={snapToGrid ? 'Snap to grid: ON — click to disable' : 'Snap to grid: OFF — click to enable'}>
                <button
                  onClick={() => setSnapToGrid(s => !s)}
                  className={`flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded transition-colors shrink-0 ${snapToGrid ? 'text-gray-300 hover:text-white hover:bg-surface-800' : 'text-gray-500 hover:text-gray-300 hover:bg-surface-800'}`}
                >
                  <span className="hidden sm:inline">Snap {snapToGrid ? 'ON' : 'OFF'}</span>
                  <span className="sm:hidden">⊞</span>
                </button>
              </Tooltip>
              <div className="w-px h-5 bg-surface-700 shrink-0" />
              <button onClick={() => setShowToolbar(!showToolbar)} className="p-1.5 hover:bg-surface-800 rounded text-xs text-gray-400 shrink-0 md:hidden">
                {showToolbar ? 'Hide' : 'Add'}
              </button>
              <div className={`flex items-center gap-1 ${showToolbar ? '' : 'hidden md:flex'}`}>
                {TOOLBAR_ITEMS.map((item, i) =>
                  item === null
                    ? <div key={`sep-${i}`} className="w-px h-5 bg-surface-700 shrink-0 mx-0.5" />
                    : (
                      <Tooltip key={item.type} label={item.label}>
                        <button
                          onClick={() => addElement(item.type)}
                          draggable={isEditable}
                          onDragStart={isEditable ? (ev) => {
                            toolbarDragTypeRef.current = item.type;
                            setToolbarDrag({ type: item.type });
                            ev.dataTransfer.effectAllowed = 'copy';
                          } : undefined}
                          onDragEnd={isEditable ? () => {
                            toolbarDragTypeRef.current = null;
                            setToolbarDrag(null);
                            setDropIndicator(null);
                          } : undefined}
                          className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-gray-400 hover:text-white hover:bg-surface-800 rounded transition-colors shrink-0"
                        >
                          <item.icon className="w-3.5 h-3.5" />
                          <span className="hidden lg:inline">{item.label}</span>
                        </button>
                      </Tooltip>
                    )
                )}
              </div>
            </>
          )}
          {readOnly && <span className="text-xs text-gray-500">View only</span>}
        </div>

        {/* Right: always-visible actions */}
        <div className="flex items-center gap-1 px-2 py-2 shrink-0 border-l border-surface-700">
          {!readOnly && (
            <>
              {isEditable && (
                <>
                  <Tooltip label="Undo (Ctrl+Z)">
                    <button onClick={undo} disabled={!canUndo} className={`p-1.5 rounded transition-colors ${canUndo ? 'hover:bg-surface-800' : 'opacity-40 cursor-not-allowed'}`}>
                      <Undo2 className={`w-4 h-4 ${canUndo ? 'text-gray-300' : 'text-gray-600'}`} />
                    </button>
                  </Tooltip>
                  <Tooltip label="Redo (Ctrl+Y)">
                    <button onClick={redo} disabled={!canRedo} className={`p-1.5 rounded transition-colors ${canRedo ? 'hover:bg-surface-800' : 'opacity-40 cursor-not-allowed'}`}>
                      <Redo2 className={`w-4 h-4 ${canRedo ? 'text-gray-300' : 'text-gray-600'}`} />
                    </button>
                  </Tooltip>
                  <div className="w-px h-5 bg-surface-700 shrink-0" />
                </>
              )}
              {saveStatus === 'unsaved' && <span className="flex items-center gap-1 text-xs text-amber-400 mr-1">Unsaved</span>}
              {saveStatus === 'saving' && <span className="flex items-center gap-1 text-xs text-blue-400 mr-1"><Loader2 className="w-3 h-3 animate-spin" />Saving...</span>}
              {saveStatus === 'saved' && <span className="flex items-center gap-1 text-xs text-green-400 mr-1"><Check className="w-3 h-3" />Saved</span>}
              {saveStatus === 'autosaved' && <span className="flex items-center gap-1 text-xs text-gray-400 mr-1"><Check className="w-3 h-3" />Autosaved</span>}
              {saveStatus === 'failed' && <span className="flex items-center gap-1 text-xs text-red-400 mr-1"><AlertTriangle className="w-3 h-3" />Save failed</span>}
              <button onClick={handleSave} disabled={saving} className="flex items-center gap-1 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-xs font-medium rounded transition-colors">
                <Save className="w-3.5 h-3.5" />
                Save
              </button>
              <div className="w-px h-5 bg-surface-700 shrink-0" />
            </>
          )}
          <select
            value={zoom}
            onChange={e => handleZoomChange(Number(e.target.value))}
            className="bg-surface-800 text-gray-300 text-xs border border-surface-600 rounded px-1.5 py-1 cursor-pointer hover:bg-surface-700 transition-colors"
            title="Zoom level"
          >
            {ZOOM_LEVELS.map(level => (
              <option key={level} value={level}>{level}%</option>
            ))}
          </select>
          {zoom !== 100 && (
            <Tooltip label="Reset to 100%">
              <button onClick={() => handleZoomChange(100)} className="text-[10px] text-gray-500 hover:text-gray-300 transition-colors px-1 shrink-0">
                Reset
              </button>
            </Tooltip>
          )}
          {onShowVersions && (
            <Tooltip label="Version History">
              <button onClick={onShowVersions} className="p-1.5 hover:bg-surface-800 rounded transition-colors">
                <History className="w-4 h-4 text-gray-400" />
              </button>
            </Tooltip>
          )}
          {onExportPDF && (
            <Tooltip label="Export PDF">
              <button onClick={onExportPDF} className="p-1.5 hover:bg-surface-800 rounded transition-colors">
                <Download className="w-4 h-4 text-gray-400" />
              </button>
            </Tooltip>
          )}
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden relative">
        <div
          ref={scrollContainerRef}
          className={`flex-1 overflow-auto p-4 ${isEditable && selectMode ? 'cursor-crosshair' : ''}`}
          onMouseDown={handleCanvasMouseDown}
          onTouchStart={handleCanvasTouchStart}
        >
          <div
            className="mx-auto"
            style={{
              width: canvasWidth * zoomFactor,
              height: canvasHeight * zoomFactor,
            }}
          >
            <div
              ref={canvasRef}
              className="relative bg-white shadow-xl"
              style={{
                width: canvasWidth,
                minHeight: canvasHeight,
                transform: `scale(${zoomFactor})`,
                transformOrigin: 'top left',
                userSelect: (isEditable && selectMode) ? 'none' : undefined,
              }}
              onDragOver={isEditable && toolbarDrag ? handleCanvasDragOver : undefined}
              onDragLeave={isEditable && toolbarDrag ? handleCanvasDragLeave : undefined}
              onDrop={isEditable && toolbarDrag ? handleCanvasDrop : undefined}
            >
            {isEditable && selectedElements.length >= 2 && (() => {
              const xs = selectedElements.map(el => el.x);
              const ys = selectedElements.map(el => el.y);
              const x2s = selectedElements.map(el => el.x + el.width);
              const y2s = selectedElements.map(el => el.y + el.height);
              const bx = Math.min(...xs) - 5;
              const by = Math.min(...ys) - 5;
              const bw = Math.max(...x2s) - Math.min(...xs) + 10;
              const bh = Math.max(...y2s) - Math.min(...ys) + 10;
              return (
                <div
                  key="group-box"
                  className="absolute pointer-events-none border-2 border-blue-500/50 rounded"
                  style={{ left: bx, top: by, width: bw, height: bh, zIndex: 9997 }}
                />
              );
            })()}
            {elements.map(el => (
              <ElementRenderer
                key={el.id}
                element={el}
                selected={isEditable && selectedIds.has(el.id)}
                inMultiSelect={isEditable && selectedIds.size > 1 && selectedIds.has(el.id)}
                dimmed={isEditable && selectMode && selectedIds.size > 0 && !selectedIds.has(el.id)}
                readOnly={!isEditable}
                autoEdit={isEditable && el.id === pendingEditId}
                onAutoEditDone={el.id === pendingEditId ? () => setPendingEditId(null) : undefined}
                onMouseDown={!isEditable ? undefined : e => handleElementMouseDown(e, el.id)}
                onTouchStart={!isEditable ? undefined : e => handleElementTouchStart(e, el.id)}
                onResize={!isEditable ? undefined : (handle, e) => handleResizeStart(el.id, handle, e)}
                onUpdateContent={!isEditable ? undefined : (content) => {
                  const currentEl = elementsRef.current.find(e => e.id === el.id);
                  if (!currentEl) {
                    updateElement(el.id, { content: { ...el.content, ...content } });
                    return;
                  }
                  const { _naturalHeight, ...actualContent } = content as Record<string, any>;
                  const newContent = { ...currentEl.content, ...actualContent };

                  if (currentEl.type === 'image') {
                    setElements(prev => prev.map(e => e.id === el.id ? { ...e, content: newContent } : e));
                    markDirty();
                    return;
                  }

                  const colBoundsEl = getColumnBounds(currentEl.x, currentEl.y, currentEl.width, currentEl.height, elementsRef.current, canvasWidth);
                  const baseMaxWidthEl = (currentEl.type === 'songHeader' || currentEl.type === 'songForm') ? HEADER_MAX_WIDTH : AUTO_RESIZE_MAX_WIDTH;
                  const effectiveMaxWidth = colBoundsEl ? colBoundsEl.maxWidth : baseMaxWidthEl;
                  const { width: autoWidth, height: measuredH } = autoSizeElement(currentEl.type, newContent, effectiveMaxWidth);
                  const fontSize = (newContent.fontSize as number) || 14;
                  const minH = Math.ceil(fontSize * 1.5) + 8;
                  // For chord elements always use measuredH: the textarea scrollHeight is captured
                  // at the old (narrower) element width and can falsely report wrapping before the
                  // element has been widened. autoSizeElement measures at the final computed width.
                  const autoHeight = (currentEl.type !== 'chord' && _naturalHeight !== undefined)
                    ? Math.max(minH, Math.round(_naturalHeight as number) + 2)
                    : measuredH;

                  // manualSize: preserve user-set width and never shrink below user-set height,
                  // but still expand (+ reflow) when text has grown beyond the stored height.
                  // Column bounds always take priority over manual width.
                  // Song Form: always preserve width during content changes; width
                  // transitions (editor↔display) are handled by onEditingChanged callback.
                  const width = (currentEl.type === 'songForm' || currentEl.manualSize)
                    ? Math.min(currentEl.width, colBoundsEl ? colBoundsEl.maxWidth : Infinity)
                    : autoWidth;
                  const height = currentEl.manualSize ? Math.max(currentEl.height, autoHeight) : autoHeight;

                  // currentEl is fetched fresh from elementsRef.current at the top of this handler.
                  // Because elementsRef.current = next is written synchronously before setElements,
                  // rapid keystrokes always see the height committed by the previous call — so
                  // currentEl.height is always an accurate baseline with no separate ref needed.
                  const heightDelta = height - currentEl.height;

                  if (heightDelta !== 0) {
                    const insertY = currentEl.y + currentEl.height;
                    const adjusted = computeInsertPushDown(
                      elementsRef.current,
                      currentEl.id,
                      insertY,
                      heightDelta,
                      currentEl.x,
                      currentEl.x + currentEl.width,
                      currentEl.y,
                      insertY + heightDelta,
                    );
                    const anyMoved = adjusted.some(e => {
                      const orig = elementsRef.current.find(o => o.id === e.id);
                      return orig && e.y !== orig.y;
                    });
                    if (anyMoved) pushUndo(elementsRef.current);
                    const next = adjusted.map(e => e.id === el.id ? { ...e, content: newContent, width, height } : e);
                    elementsRef.current = next;
                    setElements(next);
                    maybeExpandCanvas(next);
                  } else {
                    const next = elementsRef.current.map(e => e.id === el.id ? { ...e, content: newContent, width, height } : e);
                    elementsRef.current = next;
                    setElements(next);
                    maybeExpandCanvas(next);
                  }
                  markDirty();
                }}
                onEditingChanged={el.type === 'songForm' && isEditable ? (editing) => {
                  const currentEl = elementsRef.current.find(e => e.id === el.id);
                  if (!currentEl || currentEl.manualSize) return;
                  if (editing) {
                    // Editor opened: compact to 150px editor width
                    if (currentEl.width !== 150) updateElement(el.id, { width: 150 });
                  } else {
                    // Editor closed: auto-size width to display content (no wrapping until page margin)
                    const colBounds = getColumnBounds(currentEl.x, currentEl.y, currentEl.width, currentEl.height, elementsRef.current, canvasWidth);
                    const maxW = colBounds ? colBounds.maxWidth : HEADER_MAX_WIDTH;
                    const { width: displayW } = autoSizeElement(currentEl.type, currentEl.content, maxW);
                    const newW = Math.min(displayW, maxW);
                    if (newW !== currentEl.width) updateElement(el.id, { width: newW });
                  }
                } : undefined}
              />
            ))}
            {marquee && marqueeStyle && (
              <div
                className="absolute border-2 border-blue-400 bg-blue-400/10 pointer-events-none"
                style={marqueeStyle}
              />
            )}
            {dropIndicator && (
              dropIndicator.mode === 'empty' ? (
                <div
                  className="absolute rounded pointer-events-none border-2 border-dashed border-primary-400/60 bg-primary-400/10"
                  style={{ left: dropIndicator.x, top: dropIndicator.y, width: dropIndicator.w, height: Math.max(dropIndicator.h, 20), zIndex: 9998 }}
                />
              ) : (
                <div
                  className="absolute pointer-events-none rounded-full"
                  style={{
                    left: dropIndicator.x,
                    top: dropIndicator.y - 1,
                    width: dropIndicator.w,
                    height: 3,
                    zIndex: 9998,
                    background: '#3b82f6',
                    boxShadow: '0 0 0 2px rgba(59,130,246,0.25)',
                  }}
                />
              )
            )}
          </div>
          </div>
          {isEditable && (
            <div className="flex justify-center py-3" style={{ width: canvasWidth * zoomFactor, margin: '0 auto' }}>
              <button
                onClick={() => { setCanvasHeight(prev => prev + EXPAND_INCREMENT); markDirty(); }}
                className="flex items-center gap-1.5 px-4 py-1.5 text-xs text-gray-400 hover:text-gray-200 bg-surface-800 hover:bg-surface-700 border border-surface-600 rounded-full transition-colors"
              >
                <span className="text-base leading-none">+</span> Add More Space
              </button>
            </div>
          )}
        </div>

        {hasSelection && isEditable && !inspectorCollapsed && (
          <div className="flex flex-col shrink-0">
            <button
              onClick={() => setInspectorCollapsed(true)}
              className="flex items-center justify-center gap-1 px-2 py-1 bg-surface-800 border-b border-l border-surface-700 text-gray-400 hover:text-white hover:bg-surface-700 transition-colors text-xs"
              title="Hide inspector"
            >
              <PanelRightClose className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Hide</span>
            </button>
            {singleSelected ? (
              <InspectorPanel
                element={singleSelected}
                onUpdate={(updates) => {
                  const isResizeUpdate = 'width' in updates || 'height' in updates;
                  updateElement(singleSelected.id, isResizeUpdate ? { ...updates, manualSize: true } : updates);
                }}
                onUpdateContent={(content) => {
                  const currentEl = elementsRef.current.find(e => e.id === singleSelected.id);
                  if (!currentEl) return;
                  const newContent = { ...currentEl.content, ...content };
                  if (currentEl.type === 'image') {
                    updateElement(currentEl.id, { content: newContent });
                    return;
                  }
                  const colBoundsInsp = getColumnBounds(currentEl.x, currentEl.y, currentEl.width, currentEl.height, elementsRef.current, canvasWidth);
                  const baseMaxWidthInsp = (currentEl.type === 'songHeader' || currentEl.type === 'songForm') ? HEADER_MAX_WIDTH : AUTO_RESIZE_MAX_WIDTH;
                  const effectiveMaxWidthInsp = colBoundsInsp ? colBoundsInsp.maxWidth : baseMaxWidthInsp;
                  const { width: autoWidth, height: autoHeight } = autoSizeElement(currentEl.type, newContent, effectiveMaxWidthInsp);
                  // Song Form: preserve current width on inspector updates (mode toggle, etc.)
                  // to prevent collapsing to narrow display-text width.
                  const width = (currentEl.type === 'songForm' || currentEl.manualSize)
                    ? Math.min(currentEl.width, colBoundsInsp ? colBoundsInsp.maxWidth : Infinity)
                    : autoWidth;
                  const height = currentEl.manualSize ? Math.max(currentEl.height, autoHeight) : autoHeight;
                  const heightDelta = height - currentEl.height;
                  if (heightDelta !== 0) {
                    const insertY = currentEl.y + currentEl.height;
                    const adjusted = computeInsertPushDown(
                      elementsRef.current, currentEl.id, insertY, heightDelta,
                      currentEl.x, currentEl.x + currentEl.width,
                      currentEl.y, insertY + heightDelta,
                    );
                    const anyMoved = adjusted.some(e => {
                      const orig = elementsRef.current.find(o => o.id === e.id);
                      return orig && e.y !== orig.y;
                    });
                    if (anyMoved) pushUndo(elementsRef.current);
                    const next = adjusted.map(e => e.id === currentEl.id ? { ...e, content: newContent, width, height } : e);
                    elementsRef.current = next;
                    setElements(next);
                    maybeExpandCanvas(next);
                  } else {
                    const next = elementsRef.current.map(e => e.id === currentEl.id ? { ...e, content: newContent, width, height } : e);
                    elementsRef.current = next;
                    setElements(next);
                    maybeExpandCanvas(next);
                  }
                  markDirty();
                }}
                onDelete={() => deleteElement(singleSelected.id)}
                onDuplicate={() => duplicateElement(singleSelected.id)}
                onLayerUp={() => moveLayer(singleSelected.id, 'up')}
                onLayerDown={() => moveLayer(singleSelected.id, 'down')}
                onReplaceImage={singleSelected.type === 'image' ? handleReplaceImage : undefined}
                onEditElement={singleSelected.type === 'songForm'
                  ? () => setPendingEditId(singleSelected.id)
                  : undefined}
              />
            ) : (
              <div className="w-56 bg-surface-900 border-l border-surface-700 overflow-y-auto shrink-0 flex flex-col">
                <div className="px-3 py-2 border-b border-surface-700">
                  <span className="text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    {selectedElements.length} items selected
                  </span>
                </div>
                <div className="p-3 space-y-3">
                  <div className="text-xs text-gray-500">
                    {selectedElements.map(el => el.type).filter((v, i, a) => a.indexOf(v) === i).join(', ')}
                  </div>

                  <div>
                    <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Alignment</div>
                    <div className="grid grid-cols-3 gap-1">
                      <button onClick={() => alignElements('left')} className="p-1.5 hover:bg-surface-700 rounded transition-colors flex items-center justify-center" title="Align Left">
                        <AlignHorizontalJustifyStart className="w-4 h-4 text-gray-300" />
                      </button>
                      <button onClick={() => alignElements('center')} className="p-1.5 hover:bg-surface-700 rounded transition-colors flex items-center justify-center" title="Align Center">
                        <AlignHorizontalJustifyCenter className="w-4 h-4 text-gray-300" />
                      </button>
                      <button onClick={() => alignElements('right')} className="p-1.5 hover:bg-surface-700 rounded transition-colors flex items-center justify-center" title="Align Right">
                        <AlignHorizontalJustifyEnd className="w-4 h-4 text-gray-300" />
                      </button>
                      <button onClick={() => alignElements('top')} className="p-1.5 hover:bg-surface-700 rounded transition-colors flex items-center justify-center" title="Align Top">
                        <AlignVerticalJustifyStart className="w-4 h-4 text-gray-300" />
                      </button>
                      <button onClick={() => alignElements('middle')} className="p-1.5 hover:bg-surface-700 rounded transition-colors flex items-center justify-center" title="Align Middle">
                        <AlignVerticalJustifyCenter className="w-4 h-4 text-gray-300" />
                      </button>
                      <button onClick={() => alignElements('bottom')} className="p-1.5 hover:bg-surface-700 rounded transition-colors flex items-center justify-center" title="Align Bottom">
                        <AlignVerticalJustifyEnd className="w-4 h-4 text-gray-300" />
                      </button>
                    </div>
                  </div>

                  <div>
                    <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Distribute</div>
                    <div className="grid grid-cols-2 gap-1">
                      <button
                        onClick={() => distributeElements('horizontal')}
                        disabled={selectedElements.length < 3}
                        className={`p-1.5 rounded transition-colors flex items-center justify-center gap-1 ${selectedElements.length >= 3 ? 'hover:bg-surface-700' : 'opacity-40 cursor-not-allowed'}`}
                        title={selectedElements.length < 3 ? 'Select 3+ items' : 'Distribute Horizontally'}
                      >
                        <AlignHorizontalSpaceBetween className="w-4 h-4 text-gray-300" />
                        <span className="text-[10px] text-gray-400">Horiz</span>
                      </button>
                      <button
                        onClick={() => distributeElements('vertical')}
                        disabled={selectedElements.length < 3}
                        className={`p-1.5 rounded transition-colors flex items-center justify-center gap-1 ${selectedElements.length >= 3 ? 'hover:bg-surface-700' : 'opacity-40 cursor-not-allowed'}`}
                        title={selectedElements.length < 3 ? 'Select 3+ items' : 'Distribute Vertically'}
                      >
                        <AlignVerticalSpaceBetween className="w-4 h-4 text-gray-300" />
                        <span className="text-[10px] text-gray-400">Vert</span>
                      </button>
                    </div>
                  </div>

                  <div className="pt-1 border-t border-surface-700">
                    <button
                      onClick={deleteSelected}
                      className="w-full py-1.5 rounded text-xs font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
                    >
                      Delete All ({selectedElements.length})
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {isEditable && inspectorCollapsed && hasSelection && (
          <button
            onClick={() => setInspectorCollapsed(false)}
            className="absolute right-3 bottom-3 flex items-center gap-1.5 px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white text-xs font-medium rounded-lg shadow-lg transition-colors z-50"
            title="Show inspector"
          >
            <PanelRightOpen className="w-4 h-4" />
            <span>Editor</span>
          </button>
        )}
      </div>

      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" />
      <input ref={replaceImageRef} type="file" accept="image/*" className="hidden" />

      {pendingDeleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-surface-900 border border-surface-700 rounded-xl p-5 w-full max-w-sm mx-4 shadow-2xl">
            <h3 className="font-semibold text-gray-100 mb-2">Delete song header?</h3>
            <p className="text-sm text-gray-400 mb-4">
              This header is linked to the song details. You can add it again later from the toolbar.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setPendingDeleteId(null)}
                className="px-3 py-1.5 text-sm text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const id = pendingDeleteId;
                  setPendingDeleteId(null);
                  pushUndo(elementsRef.current);
                  setElements(prev => prev.filter(e => e.id !== id));
                  clearSelection();
                  markDirty();
                }}
                className="px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
